/**
 * 자재 거래처 거래이력(서랍 4줄) 문구·판정 로직 — 순수 함수 (WP5·6단계 U3, §10).
 * 입력은 Planner `GET /schedule/vendors/{vendorId}/order-history` (VendorOrderHistoryItem).
 * 값이 없으면 '—'(muted). 0·기본값으로 채우지 않는다.
 */
import type { VendorOrderHistoryItem } from '../api/vendors';

export type VendorPhase = 'UPCOMING' | 'ONGOING' | 'DONE';
export type VendorTimelineRow = { label: string; value: string; muted?: boolean; tone?: 'ok' };

const DASH = '—';
const MIN = 60_000;
const DAY = 86_400_000;

/** 'YYYY-MM-DD[THH:mm[:ss]]' → 구성요소 (시간대 변환 없이 문자열 그대로 해석) */
function parts(s: string | null | undefined): { y: number; mo: number; d: number; h: number; mi: number; hasTime: boolean } | null {
  const m = s ? /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s) : null;
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : 0, mi: m[5] ? +m[5] : 0, hasTime: !!m[4] };
}

function epoch(s: string | null | undefined): number | null {
  const p = parts(s);
  return p ? Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi) : null;
}

/** 'M/D' */
export function vendorFormatMd(s: string | null | undefined): string | null {
  const p = parts(s);
  return p ? `${p.mo}/${p.d}` : null;
}

/** 'M/D HH:mm' (시각 없으면 'M/D') */
export function vendorFormatMdHm(s: string | null | undefined): string | null {
  const p = parts(s);
  if (!p) return null;
  if (!p.hasTime) return `${p.mo}/${p.d}`;
  return `${p.mo}/${p.d} ${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;
}

/** 경과 분 → '45분' / '2시간' / '2시간 30분' / '1일 3시간' */
export function vendorFormatElapsed(minutes: number): string {
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    return m === 0 ? `${hours}시간` : `${hours}시간 ${m}분`;
  }
  return `${Math.floor(hours / 24)}일 ${hours % 24}시간`;
}

/** 요청→회신 경과 분. 회신이 요청보다 이르면(이상 행) null */
function elapsedMinutes(from: string | null | undefined, to: string | null | undefined): number | null {
  const a = epoch(from);
  const b = epoch(to);
  if (a == null || b == null || b < a) return null;
  return Math.floor((b - a) / MIN);
}

function elapsedAfter(from: string | null | undefined, to: string | null | undefined): string {
  const m = elapsedMinutes(from, to);
  return m == null ? '' : ` (${vendorFormatElapsed(m)} 뒤)`;
}

/** 발주정보 줄 */
export function vendorOrderInfoRow(h: VendorOrderHistoryItem): VendorTimelineRow {
  const label = '발주정보';
  const r = h.orderRequest;
  const sent = r ? vendorFormatMdHm(r.sentDtm) : null;
  if (!r || !sent) return { label, value: DASH, muted: true };
  const req = `요청 ${sent}`;
  if (r.replyResultCode !== 'ACCEPTED' && r.replyResultCode !== 'DECLINED') {
    return { label, value: `${req} → 회신 대기` };
  }
  const word = r.replyResultCode === 'ACCEPTED' ? '회신 가능' : '회신 불가';
  const at = vendorFormatMdHm(r.replyDtm);
  const mins = elapsedMinutes(r.sentDtm, r.replyDtm);
  return {
    label,
    value: `${req} → ${word}${at ? ` ${at}` : ''}${elapsedAfter(r.sentDtm, r.replyDtm)}`,
    tone: mins != null && mins <= 120 ? 'ok' : undefined,
  };
}

/** 양중요청 줄 */
export function vendorLiftRow(h: VendorOrderHistoryItem): VendorTimelineRow {
  const label = '양중요청';
  if (h.liftReqYn !== 'Y') return { label, value: '양중요청 : N' };
  if (h.orderRequest?.replyResultCode === 'DECLINED') return { label, value: '양중요청 : Y → 회신 : –' };
  if (h.liftAvailYn === 'Y') return { label, value: '양중요청 : Y → 회신 : Y (양중가능)' };
  if (h.liftAvailYn === 'N') return { label, value: '양중요청 : Y → 회신 : N (양중불가)' };
  return { label, value: '양중요청 : Y → 회신 : –' };
}

/** 배송정보 줄 */
export function vendorShipRow(h: VendorOrderHistoryItem): VendorTimelineRow {
  const label = '배송정보';
  const s = h.shipRequest;
  const sent = s ? vendorFormatMdHm(s.sentDtm) : null;
  if (!s || !sent) return { label, value: '요청 – → 회신 –', muted: true };
  const replied = vendorFormatMdHm(s.replyDtm);
  if (!replied) return { label, value: `요청 ${sent} → 회신 대기` };
  return { label, value: `요청 ${sent} → 회신 ${replied}${elapsedAfter(s.sentDtm, s.replyDtm)}` };
}

/**
 * 입고준수 줄 — 요청=입고 희망일(입고 시점 스냅샷, 입고 전이면 현재 희망일).
 * 입고가 끝났는데 약속일(promisedDate)이 없으면 현재 입고희망일로 대체하지 않고 줄을 생략한다(null).
 */
export function vendorReceiptRow(h: VendorOrderHistoryItem): VendorTimelineRow | null {
  const label = '입고준수';
  const rc = h.receipt;
  const recMd = rc ? vendorFormatMd(rc.receiptDate) : null;
  if (rc && recMd) {
    const promised = rc.promisedDate;
    const reqMd = vendorFormatMd(promised);
    const a = epoch(promised);
    const b = epoch(rc.receiptDate);
    if (!promised || reqMd == null || a == null || b == null) return null;
    const late = Math.round((b - a) / DAY);
    return {
      label,
      value: `요청 ${reqMd} → 입고 ${recMd} (${late > 0 ? `입고지연 ${late}일` : '준수'})`,
      tone: late > 0 ? undefined : 'ok',
    };
  }
  const reqMd = vendorFormatMd(h.deliveryDate) ?? '–';
  if (h.orderStatusCode === 'DECLINED') return { label, value: `요청 ${reqMd} → 입고 – (재배정 필요)` };
  return { label, value: `요청 ${reqMd} → 입고 – (입고 전)`, muted: !vendorFormatMd(h.deliveryDate) };
}

/** 4줄 한 번에 */
export function vendorTimelineRows(h: VendorOrderHistoryItem): VendorTimelineRow[] {
  const receipt = vendorReceiptRow(h);
  return [vendorOrderInfoRow(h), vendorLiftRow(h), vendorShipRow(h), ...(receipt ? [receipt] : [])];
}

const SEOUL_DATE_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });

/** 서울 기준 날짜(그 순간의 서울 달력 날짜)를 UTC 자정 epoch 로. 기기 시간대와 무관 */
function seoulDayEpoch(d: Date): number {
  const [y, m, day] = SEOUL_DATE_FMT.format(d).split('-').map(Number);
  return Date.UTC(y, m - 1, day);
}

/**
 * 진행 3상태(배지 표시용, 평가 가능 여부와 무관) — RECEIVED=완료, 그 외 발주일이 오늘 이후면 예정, 아니면 진행중.
 * 오늘은 서울 기준(기기 시간대 아님). today 를 생략하면 지금 시각의 서울 날짜.
 */
export function vendorPhase(h: VendorOrderHistoryItem, orderedDate: string | null, today: Date = new Date()): VendorPhase {
  if (h.orderStatusCode === 'RECEIVED') return 'DONE';
  const o = epoch(orderedDate);
  const t = seoulDayEpoch(today);
  return o != null && o > t ? 'UPCOMING' : 'ONGOING';
}

/**
 * 평가 잠금 — 현장이 완료면 서버가 reviewLocked=true 로 내려준다.
 * 필드가 없거나 true 가 아니면 잠그지 않는다(진행상태·입고 여부와 무관).
 */
export function isReviewLocked(item: { reviewLocked?: boolean | null }): boolean {
  return item.reviewLocked === true;
}
